import { Delete } from '@mui/icons-material';
import { Alert, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { SingleForm } from '../../../pages/motive-manage-surveys';
import CsvUploadField from './csv-upload-field';
import type { CsvImportSummary, CsvPreviewRow } from './use-survey-management';

interface Props {
  csvLoading: boolean;
  csvImportSummary: CsvImportSummary | null;
  csvPreviewRows: CsvPreviewRow[];
  sendInvitationsFormState: SingleForm[];

  formChangeHandlerGenerator(idx: number, type: string): (e: React.ChangeEvent<HTMLInputElement>) => void;

  formChangeHandleDeleteGenerator(idx: number): () => void;

  addInvitations(): void;
  clearInvitations(): void;

  addInvitationsCSV(event: React.SyntheticEvent): void;
  addInvitationsCSVFromDrop(files: File[]): void;
}

const CreateSurveyForm: React.FC<Props> = ({
  csvLoading,
  csvImportSummary,
  csvPreviewRows,
  sendInvitationsFormState,
  formChangeHandlerGenerator,
  formChangeHandleDeleteGenerator,
  addInvitations,
  clearInvitations,
  addInvitationsCSV,
  addInvitationsCSVFromDrop,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const helperTextGenerator = (idx: number) => {
    return sendInvitationsFormState[idx].email.error ? t('invalidEmail') : '';
  };

  const getCsvSummarySeverity = () => {
    if (!csvImportSummary) return 'success';
    if (csvImportSummary.hasError) return 'error';
    if (csvImportSummary.invalidEmailCount > 0 || csvImportSummary.duplicateCount > 0 || csvImportSummary.ignoredRowCount > 0) return 'warning';
    return 'success';
  };

  const getStatusChip = (status: CsvPreviewRow['status']) => {
    if (status === 'valid') return <Chip size={'small'} color={'success'} label={t('csvStatusValid')} />;
    if (status === 'invalid_email') return <Chip size={'small'} color={'warning'} label={t('csvStatusInvalidEmail')} />;
    if (status === 'duplicate') return <Chip size={'small'} color={'default'} label={t('csvStatusDuplicate')} />;
    if (status === 'ignored') return <Chip size={'small'} color={'default'} label={t('csvStatusIgnored')} />;
    return <Chip size={'small'} color={'error'} label={t('csvStatusTruncated')} />;
  };

  return (
    <Stack gap={2}>
      <Stack gap={1}>
        {csvLoading ? (
          <PaiDotLoader />
        ) : (
          sendInvitationsFormState.map((val, idx) => (
            <Stack key={`form-${idx}`} direction={'row'} gap={1} alignItems={'center'}>
              <PaiTextField
                value={sendInvitationsFormState[idx].email.text}
                placeholder={t('email')}
                onChange={formChangeHandlerGenerator(idx, 'email')}
                error={sendInvitationsFormState[idx].email.error}
                errorText={helperTextGenerator(idx)}
                disabled={csvLoading}
              />
              <PaiTextField
                value={sendInvitationsFormState[idx].lastName.text}
                placeholder={t('lastName')}
                onChange={formChangeHandlerGenerator(idx, 'lastName')}
                disabled={csvLoading}
              />
              <PaiTextField
                value={sendInvitationsFormState[idx].firstName.text}
                placeholder={t('firstName')}
                onChange={formChangeHandlerGenerator(idx, 'firstName')}
                disabled={csvLoading}
              />
              {sendInvitationsFormState.length > 1 ? (
                <IconButton onClick={formChangeHandleDeleteGenerator(idx)} size={'small'}>
                  <Delete className={classes.icon} />
                </IconButton>
              ) : null}
            </Stack>
          ))
        )}
      </Stack>
      <Stack direction={'row'} gap={2} alignItems={'center'} flexWrap={'wrap'}>
        <PaiLink text={t('addAnother')} size={'md'} variant={'primary'} action={addInvitations} ml={'4px'} />
        <PaiLink text={t('csvUploadResetList')} size={'md'} variant={'default'} action={clearInvitations} ml={'4px'} />
      </Stack>
      <CsvUploadField addInvitationsCSV={addInvitationsCSV} addInvitationsCSVFromDrop={addInvitationsCSVFromDrop} csvLoading={csvLoading} />
      {csvImportSummary ? (
        <Alert severity={getCsvSummarySeverity()}>
          <Stack gap={0.5}>
            <Typography variant={'body2'} fontWeight={700}>
              {csvImportSummary.hasError
                ? t('csvUploadNoRows', { file: csvImportSummary.fileName })
                : t('csvUploadImportedRows', { count: csvImportSummary.importedCount, file: csvImportSummary.fileName })}
            </Typography>
            {csvImportSummary.hasHeader ? <Typography variant={'caption'}>{t('csvUploadHeaderDetected')}</Typography> : null}
            {csvImportSummary.invalidEmailCount > 0 ? (
              <Typography variant={'caption'}>{t('csvUploadInvalidEmails', { count: csvImportSummary.invalidEmailCount })}</Typography>
            ) : null}
            {csvImportSummary.duplicateCount > 0 ? (
              <Typography variant={'caption'}>{t('csvUploadDuplicatesIgnored', { count: csvImportSummary.duplicateCount })}</Typography>
            ) : null}
            {csvImportSummary.ignoredRowCount > 0 ? (
              <Typography variant={'caption'}>{t('csvUploadRowsIgnored', { count: csvImportSummary.ignoredRowCount })}</Typography>
            ) : null}
            {csvImportSummary.truncatedCount > 0 ? (
              <Typography variant={'caption'}>{t('csvUploadLimitReached', { count: csvImportSummary.truncatedCount })}</Typography>
            ) : null}
          </Stack>
        </Alert>
      ) : null}
      {csvPreviewRows.length > 0 ? (
        <Stack gap={1}>
          <Typography variant={'subtitle2'}>{t('csvPreviewTitle')}</Typography>
          <TableContainer component={Paper} variant={'outlined'}>
            <Table size={'small'}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('csvPreviewRow')}</TableCell>
                  <TableCell>{t('email')}</TableCell>
                  <TableCell>{t('lastName')}</TableCell>
                  <TableCell>{t('firstName')}</TableCell>
                  <TableCell>{t('status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {csvPreviewRows.map((row) => (
                  <TableRow key={`${row.rowNumber}-${row.email}-${row.lastName}-${row.firstName}`}>
                    <TableCell>{row.rowNumber}</TableCell>
                    <TableCell>{row.email || '-'}</TableCell>
                    <TableCell>{row.lastName || '-'}</TableCell>
                    <TableCell>{row.firstName || '-'}</TableCell>
                    <TableCell>{getStatusChip(row.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {csvPreviewRows.length >= 200 ? <Typography variant={'caption'}>{t('csvPreviewLimitHint')}</Typography> : null}
        </Stack>
      ) : null}
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  icon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
}));

export default CreateSurveyForm;
